import { last, omit } from 'ramda';
import { useInfiniteQuery, useQuery } from 'react-query';
import { useSelector } from 'react-redux';
import { isDebugModeSelector } from '../../redux/reducers/app/initialization';
import { isNativeSelector } from '../../redux/reducers/app/params';
import { getAPI } from '../../services/API';
import { sdkErrorHandler } from '../../utils';
import { useLobbyURL } from '../useLobbyURL';
import { DEFAULT_QUERY_PARAMS } from './constants';
import { collectingCampaignIdSelector } from './selectors/bonuses';
import { useBonusesDataSelector } from './useQueryBonuses';
import useRequestConfig from './useRequestConfig';
import { getStartQueryPage, timeoutHandler } from './utils';
import { useIsUnifiedPromoBonusSystemEnabled } from '../useIsUnifiedPromoBonusSystemEnabled';

export const QUERY_KEY_GAME = 'game';
export const QUERY_KEY_GAME_RANDOM = 'game-random';
export const QUERY_KEY_GAMES_BY_IDS = 'games-by-ids';
export const QUERY_KEY_GAMES = 'games-list';
export const QUERY_KEY_GAME_INFO = 'game-info';
export const QUERY_KEY_INFINITY_GAMES = 'infinity-games';

// TODO: temporary solution for task BL-82240
export const getGameRunUrl = (urlString, providerId, isNative) => {
  const MATCHED_PROVIDER_ID = 'belatragames';
  const isBelatraProvider = providerId.startsWith(MATCHED_PROVIDER_ID);

  if (isNative && isBelatraProvider) {
    const url = new URL(urlString);
    url.searchParams.append('wrapInFrame', '1');
    return url.toString();
  }

  return urlString;
};

export const useQueryGames = ({
  query = {},
  queryOptions = {},
  requestConfig = {},
}) => {
  const defaultRequestConfig = useRequestConfig();
  const isDebugMode = useSelector(isDebugModeSelector);

  const queryParams = {
    status: isDebugMode ? 'ACTIVE,DEBUG' : 'ACTIVE',
    ...DEFAULT_QUERY_PARAMS,
    ...query,
  };

  const fetch = ({ signal }) =>
    Promise.race([
      getAPI()
        .games.getAll({
          query: queryParams,
          signal,
          ...defaultRequestConfig,
          ...requestConfig,
        })
        .catch(
          sdkErrorHandler({
            ...defaultRequestConfig,
            ...requestConfig,
            queryOptions,
            query: queryParams,
            source: `SDK API games.getAll`,
          })
        ),
      timeoutHandler(),
    ]);

  return useQuery(
    [QUERY_KEY_GAMES, queryParams, requestConfig.headers],
    fetch,
    queryOptions
  );
};

export const useQueryGameInfo = (gameId, queryOptions = {}) => {
  const requestConfig = useRequestConfig();
  const isPromoSystemEnabled = useIsUnifiedPromoBonusSystemEnabled();

  const query = {
    ...(requestConfig?.query || {}),
    ...(isPromoSystemEnabled ? { isUnifiedLobby: true } : {}),
  };

  const fetch = ({ signal }) =>
    getAPI()
      .games.getInfo(gameId, {
        signal,
        apiVersion: 'v2',
        ...requestConfig,
        query,
      })
      .catch(
        sdkErrorHandler({
          ...requestConfig,
          queryOptions,
          query,
          gameId,
          apiVersion: 'v2',
          source: `SDK API games.getInfo`,
        })
      );

  return useQuery([QUERY_KEY_GAME_INFO, gameId, requestConfig.headers], fetch, {
    staleTime: 1000 * 60 * 5,
    cacheTime: 1000 * 60 * 5,
    ...queryOptions,
  });
};

export const useQueryGame = (gameId, isDemo = false, queryOptions = {}) => {
  const requestConfig = useRequestConfig();
  const lobbyUrl = useLobbyURL();
  const isNative = useSelector(isNativeSelector);

  const query = {};

  if (typeof lobbyUrl === 'string') {
    query.lobbyUrl = lobbyUrl;
  }

  const fetch = ({ signal }) =>
    isDemo
      ? getAPI()
          .games.getDemo(gameId, { query, signal, ...requestConfig })
          .catch(
            sdkErrorHandler({
              ...requestConfig,
              queryOptions,
              query,
              gameId,
              source: `SDK API games.getDemo`,
            })
          )
      : getAPI()
          .games.getReal(gameId, { query, signal, ...requestConfig })
          .catch(
            sdkErrorHandler({
              ...requestConfig,
              queryOptions,
              query,
              gameId,
              source: `SDK API games.getReal`,
            })
          );

  return useQuery(
    [QUERY_KEY_GAME, gameId, isDemo, requestConfig.headers],
    fetch,
    {
      staleTime: 0,
      cacheTime: 0,
      ...queryOptions,
      select: (data) => {
        const {
          url,
          backgroundImage,
          game,
          launchPayload,
          currencyConversion,
          limits,
        } = data;

        return {
          url: getGameRunUrl(url, game.provider, isNative),
          backgroundImage,
          game: {
            isDemo,
            ...game,
          },
          limits,
          currencyConversion,
          launchPayload,
        };
      },
    }
  );
};

export const useInfinityQueryGames = (query = {}, queryOptions = {}) => {
  const requestConfig = useRequestConfig();
  const isDebugMode = useSelector(isDebugModeSelector);

  const queryParams = {
    status: isDebugMode ? 'ACTIVE,DEBUG' : 'ACTIVE',
    ...DEFAULT_QUERY_PARAMS,
    ...omit(['page'], query),
  };

  const fetch = ({ pageParam = getStartQueryPage(query), signal }) => {
    const fetchQueryParams = {
      ...queryParams,
      _start: pageParam * queryParams._limit,
    };
    return getAPI()
      .games.getAll({
        query: fetchQueryParams,
        signal,
        ...requestConfig,
      })
      .catch(
        sdkErrorHandler({
          ...requestConfig,
          queryOptions,
          query: fetchQueryParams,
          source: `SDK API games.getAll`,
        })
      );
  };

  const infiniteQuery = useInfiniteQuery(
    [QUERY_KEY_INFINITY_GAMES, queryParams, query.page, requestConfig.headers],
    fetch,
    {
      ...queryOptions,
      select: (data) => {
        const pagesInfo = last(
          data.pages.map((response) => omit(['items'], response))
        );

        return {
          pagesInfo,
          pages: [...data.pages.map((response) => response.items)].flat(),
          pageParams: data.pageParams,
        };
      },
      getNextPageParam: (pageData) => {
        return pageData.totalPages > pageData.currentPage
          ? pageData.currentPage
          : undefined;
      },
    }
  );

  const data = {
    pagesInfo: infiniteQuery?.data?.pagesInfo ?? {},
    pages: infiniteQuery?.data?.pages ?? [],
  };

  return { ...infiniteQuery, data };
};

export const useInfinityQueryBonusGamesWagering = (
  query = {},
  queryOptions = {}
) => {
  return useInfinityQueryGames(query, queryOptions);
};

export const useInfinityQueryBonusGamesCollecting = (
  campaignIdParams,
  query = {},
  queryOptions = {}
) => {
  const requestConfig = useRequestConfig();
  const isDebugMode = useSelector(isDebugModeSelector);

  const { campaignId: campaignIdState } = useBonusesDataSelector(
    collectingCampaignIdSelector
  );

  const campaignId = campaignIdParams ?? campaignIdState;

  const queryParams = {
    status: isDebugMode ? 'ACTIVE,DEBUG' : 'ACTIVE',
    ...DEFAULT_QUERY_PARAMS,
    ...query,
  };

  const fetch = ({ pageParam = 0, signal }) => {
    const fetchQueryParams = {
      ...queryParams,
      _start: pageParam * queryParams._limit,
    };
    return getAPI()
      .bonusGames.getCollectingGames(campaignId, {
        signal,
        query: fetchQueryParams,
        ...requestConfig,
      })
      .catch(
        sdkErrorHandler({
          ...requestConfig,
          queryOptions,
          query: fetchQueryParams,
          campaignId,
          source: `SDK API bonusGames.getCollectingGames`,
        })
      );
  };

  const infiniteQuery = useInfiniteQuery(
    [QUERY_KEY_INFINITY_GAMES, campaignId, queryParams, requestConfig.headers],
    fetch,
    {
      ...queryOptions,
      enabled: Boolean(campaignId),
      select: (data) => {
        return {
          pages: data.pages.map((response) => response.items),
          pageParams: data.pageParams,
        };
      },
      getNextPageParam: (pageData) => {
        return pageData.totalPages > pageData.currentPage
          ? pageData.currentPage
          : undefined;
      },
    }
  );

  const data = (infiniteQuery.data?.pages || []).flat();

  return { ...infiniteQuery, data, pages: infiniteQuery.data?.pages };
};

export const useQueryGameRandom = (category, queryOptions = {}) => {
  const requestConfig = useRequestConfig();
  const isDebugMode = useSelector(isDebugModeSelector);

  const queryParams = {
    status: isDebugMode ? 'ACTIVE,DEBUG' : 'ACTIVE',
    category,
  };

  const fetch = ({ signal }) =>
    Promise.race([
      getAPI()
        .games.getRandom({
          query: queryParams,
          signal,
          ...requestConfig,
        })
        .catch(
          sdkErrorHandler({
            ...requestConfig,
            queryOptions,
            query: queryParams,
            source: `SDK API games.getRandom`,
          })
        ),
      timeoutHandler(),
    ]);

  return useQuery(
    [QUERY_KEY_GAME_RANDOM, queryParams, requestConfig.headers],
    fetch,
    {
      cacheTime: 0,
      ...queryOptions,
    }
  );
};

export const useQueryGamesByIds = (gameIds, queryOptions = {}) => {
  const requestConfig = useRequestConfig();
  const limit = gameIds?.length || 0;
  const queryParams = { _start: 0, _limit: limit };

  const fetch = ({ signal }) =>
    getAPI()
      .games.getGamesByIds(gameIds, {
        query: queryParams,
        signal,
        ...requestConfig,
      })
      .catch(
        sdkErrorHandler({
          ...requestConfig,
          queryOptions,
          gameIds,
          query: queryParams,
          source: `SDK API games.getGamesByIds`,
        })
      );

  return useQuery(
    [
      QUERY_KEY_GAMES_BY_IDS,
      gameIds,
      queryParams,
      limit,
      requestConfig.headers,
    ],
    fetch,
    {
      ...queryOptions,
      enabled: Boolean(limit),
    }
  );
};
